import { useEffect, useRef } from 'react';
import './VoiceHalo.css';

// Palette from styles/theme.css; canvas cannot read custom properties cheaply.
const GOLD_100 = '249, 233, 189';
const GOLD_300 = '232, 197, 109';

/**
 * One ring per band of the voice, low to high. Each is a band of light
 * around the circle whose swell follows its band's loudness, so vowels
 * (low/mid) push out the broad rings and consonants (high) ripple the fine
 * ones. `lobes` is how many swells run round the ring (whole numbers, so it
 * closes); `drift` the base speed they travel at.
 */
const LAYERS = [
  { band: [90, 300], lobes: 2, drift: 0.5, tint: GOLD_300, fill: 0.18, line: 0.6 },
  { band: [300, 750], lobes: 3, drift: -0.7, tint: GOLD_100, fill: 0.15, line: 0.75 },
  { band: [750, 1600], lobes: 4, drift: 0.9, tint: '171, 144, 234', fill: 0.13, line: 0.5 },
  { band: [1600, 3200], lobes: 5, drift: -1.1, tint: '122, 91, 196', fill: 0.11, line: 0.42 },
  { band: [3200, 6000], lobes: 7, drift: 1.4, tint: GOLD_300, fill: 0.07, line: 0.32 }
];

const POINTS = 120;         // samples round the circle per ring
const TRACE_POINTS = 64;    // samples of the real waveform, mirrored round the circle
const BREATH_PERIOD = 4.6;  // seconds
const FADE_OUT = 1.6;       // per second, exponential
const TAU = Math.PI * 2;

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Normalised loudness 0..1 of the spectrum between two frequencies. */
const bandEnergy = (spectrum, [low, high], hzPerBin) => {
  const from = Math.max(1, Math.round(low / hzPerBin));
  const to = Math.min(spectrum.length - 1, Math.max(from, Math.round(high / hzPerBin)));
  let sum = 0;
  for (let i = from; i <= to; i++) sum += spectrum[i];
  const mean = sum / (to - from + 1) / 255;
  // Lift off a noise floor so pauses between words read as true silence
  return Math.min(1, Math.max(0, (mean - 0.12) / 0.62));
};

/** Traces a closed curve through points, smoothed through their midpoints. */
const smoothLoop = (ctx, xs, ys, n) => {
  ctx.moveTo((xs[n - 1] + xs[0]) / 2, (ys[n - 1] + ys[0]) / 2);
  for (let p = 0; p < n; p++) {
    const q = (p + 1) % n;
    ctx.quadraticCurveTo(xs[p], ys[p], (xs[p] + xs[q]) / 2, (ys[p] + ys[q]) / 2);
  }
  ctx.closePath();
};

/** One frame. Sizes are CSS pixels; the context is already DPR-scaled. */
const draw = (ctx, w, h, state, t) => {
  ctx.clearRect(0, 0, w, h);
  if (state.fade < 0.004) return;

  const cx = w / 2;
  const cy = h / 2;
  const size = Math.min(w, h);
  const base = size * 0.25;   // the circle the voice rises from
  const reach = size * 0.2;   // how far a full-voice swell travels
  const breath = 0.5 + 0.5 * Math.sin((t * TAU) / BREATH_PERIOD);
  // At rest (paused, or waiting for the voice) the broad rings breathe
  // softly; while speaking, silence is allowed to fall back to a circle.
  const rest = state.playing ? 0 : 0.06 + 0.06 * breath;
  const { xs, ys } = state;

  ctx.globalAlpha = state.fade;
  ctx.lineJoin = 'round';

  // The engraved circle every ring grows from
  ctx.strokeStyle = `rgba(${GOLD_300}, 0.3)`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, base, 0, TAU);
  ctx.stroke();

  ctx.globalCompositeOperation = 'lighter';

  // Layered rings, broadest first. Each swells outward and, mirrored, a
  // little inward, and the band between is filled with soft light.
  LAYERS.forEach((layer, i) => {
    const amp = reach * Math.max(state.bands[i], i < 2 ? rest * (1 - i * 0.4) : 0);
    if (amp < 0.25) return;

    const phase = state.phases[i];
    const outer = state.curve;
    for (let p = 0; p < POINTS; p++) {
      const a = (p / POINTS) * TAU;
      // Two interfering lobe patterns, so the swell looks organic, not geometric
      const s = Math.sin(layer.lobes * a + phase) * Math.cos((layer.lobes - 1) * a - phase * 0.6);
      outer[p] = 0.25 + 0.75 * (0.5 + 0.5 * s);
    }

    ctx.beginPath();
    for (let p = 0; p < POINTS; p++) {
      const a = (p / POINTS) * TAU;
      const r = base + amp * outer[p];
      xs[p] = cx + Math.cos(a) * r;
      ys[p] = cy + Math.sin(a) * r;
    }
    smoothLoop(ctx, xs, ys, POINTS);

    // The inner mirror, wound the other way so the fill is a band, not a disc
    for (let p = 0; p < POINTS; p++) {
      const a = -(p / POINTS) * TAU;
      const r = base - amp * 0.45 * outer[(POINTS - p) % POINTS];
      xs[p] = cx + Math.cos(a) * r;
      ys[p] = cy + Math.sin(a) * r;
    }
    smoothLoop(ctx, xs, ys, POINTS);

    const glow = ctx.createRadialGradient(cx, cy, Math.max(0, base - amp * 0.5), cx, cy, base + amp);
    glow.addColorStop(0, `rgba(${layer.tint}, 0)`);
    glow.addColorStop(0.35, `rgba(${layer.tint}, ${layer.fill})`);
    glow.addColorStop(1, `rgba(${layer.tint}, 0)`);
    ctx.fillStyle = glow;
    ctx.fill();

    ctx.strokeStyle = `rgba(${layer.tint}, ${layer.line * Math.min(1, 0.4 + state.bands[i] * 1.5)})`;
    ctx.lineWidth = i === 1 ? 1.3 : 0.9;
    ctx.stroke();
  });

  // The voice itself: the real waveform, smoothed, wrapped round the circle
  // as a fine bright thread. It is mirrored left to right so it closes.
  if (state.traceLevel > 0.01) {
    const n = TRACE_POINTS * 2;
    for (let p = 0; p < n; p++) {
      const a = (p / n) * TAU - Math.PI / 2;
      const q = p < TRACE_POINTS ? p : n - 1 - p;
      const r = base * 0.8 + state.trace[q] * reach * 0.6;
      xs[p] = cx + Math.cos(a) * r;
      ys[p] = cy + Math.sin(a) * r;
    }
    ctx.strokeStyle = `rgba(${GOLD_100}, ${Math.min(0.85, 0.25 + state.traceLevel)})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    smoothLoop(ctx, xs, ys, n);
    ctx.stroke();
  }

  // A diamond at the centre, the ornament used on every rule in the app,
  // glowing brighter as the voice rises
  const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, base * (0.35 + 0.55 * state.level));
  halo.addColorStop(0, `rgba(${GOLD_300}, ${0.3 + 0.45 * state.level})`);
  halo.addColorStop(1, `rgba(${GOLD_300}, 0)`);
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(cx, cy, base, 0, TAU);
  ctx.fill();

  ctx.globalCompositeOperation = 'source-over';
  const d = Math.max(2.5, size * 0.022);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(Math.PI / 4);
  ctx.fillStyle = `rgb(${GOLD_100})`;
  ctx.fillRect(-d, -d, d * 2, d * 2);
  ctx.restore();

  ctx.globalAlpha = 1;
};

/**
 * The narrator's voice drawn as light round a circle: layered rings whose
 * swell follows each band of the speech, with the real waveform wrapped
 * inside them. Words push the rings out, the gaps between them let it fall
 * back to a plain circle, a pause settles it into a slow breath, and it
 * fades away when narration ends.
 *
 * Drawn imperatively on one canvas from requestAnimationFrame; React only
 * says whether it is `active` (shown) and `playing` (reading the analyser).
 * The loop stops once faded out or scrolled off screen. `onVisibleChange`
 * hears when it scrolls in or out of view; `size` is 'large' or 'small'.
 */
function VoiceHalo({ analyser, active, playing, size = 'large', onVisibleChange }) {
  const canvasRef = useRef(null);
  const props = useRef({ analyser, active, playing });
  const visibility = useRef(onVisibleChange);
  visibility.current = onVisibleChange;
  const wake = useRef(() => {});

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = prefersReducedMotion();
    const state = {
      fade: 0,
      level: 0,
      traceLevel: 0,
      playing: false,
      bands: new Float32Array(LAYERS.length),
      phases: LAYERS.map((_, i) => i * 1.3),
      curve: new Float32Array(POINTS),
      xs: new Float32Array(Math.max(POINTS, TRACE_POINTS * 2)),
      ys: new Float32Array(Math.max(POINTS, TRACE_POINTS * 2)),
      trace: new Float32Array(TRACE_POINTS),
      w: 0,
      h: 0,
      visible: true,
      running: false,
      frame: 0,
      last: 0,
      count: 0
    };
    let wave = null;
    let spectrum = null;

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      state.w = w;
      state.h = h;
    };

    // Reduced motion: one still frame per state change, no loop.
    const still = () => {
      const { active: on, playing: live } = props.current;
      state.fade = on ? 1 : 0;
      state.playing = live;
      state.level = live ? 0.3 : 0;
      state.traceLevel = 0;
      state.bands.fill(0);
      if (live) state.bands.set([0.3, 0.22, 0.12]);
      if (state.w) draw(ctx, state.w, state.h, state, 0);
    };

    /** Reads the analyser into band levels, loudness and the smoothed trace. */
    const listen = (node) => {
      if (!wave || wave.length !== node.fftSize) {
        wave = new Uint8Array(node.fftSize);
        spectrum = new Uint8Array(node.frequencyBinCount);
      }
      node.getByteTimeDomainData(wave);
      node.getByteFrequencyData(spectrum);
      const hzPerBin = node.context.sampleRate / node.fftSize;

      let sum = 0;
      let peak = 0;
      for (let i = 0; i < wave.length; i++) {
        const sample = (wave[i] - 128) / 128;
        sum += sample * sample;
        peak = Math.max(peak, Math.abs(sample));
      }
      const rms = Math.min(1, Math.sqrt(sum / wave.length) * 4.5);

      // Downsample the waveform by averaging windows (which also strips
      // the hiss), normalise it to its peak, and ease it frame to frame so
      // it reads as a voice rather than noise.
      const span = wave.length / TRACE_POINTS;
      const gain = rms > 0.02 ? 0.8 / Math.max(peak, 0.08) : 0;
      for (let p = 0; p < TRACE_POINTS; p++) {
        let acc = 0;
        const start = Math.floor(p * span);
        const end = Math.floor((p + 1) * span);
        for (let i = start; i < end; i++) acc += wave[i] - 128;
        const target = (acc / Math.max(1, end - start) / 128) * gain * Math.min(1, rms * 1.4);
        state.trace[p] += (target - state.trace[p]) * 0.45;
      }

      return { rms, hzPerBin };
    };

    const step = (now) => {
      const { analyser: node, active: on, playing: live } = props.current;
      const dt = state.last ? Math.min(0.05, (now - state.last) / 1000) : 0.016;
      state.last = now;
      const t = now / 1000;

      state.fade += ((on ? 1 : 0) - state.fade) * Math.min(1, dt * (on ? 3 : FADE_OUT));
      state.playing = live;

      let rms = 0;
      let hzPerBin = 0;
      if (live && node) ({ rms, hzPerBin } = listen(node));
      else state.trace.fill(0);

      // Quick to rise, a little slower to settle, so syllables pulse
      state.level += (rms - state.level) * (rms > state.level ? 0.4 : 0.12);
      state.traceLevel += ((live ? rms : 0) - state.traceLevel) * 0.2;

      LAYERS.forEach((layer, i) => {
        const target = hzPerBin ? bandEnergy(spectrum, layer.band, hzPerBin) : 0;
        state.bands[i] += (target - state.bands[i]) * (target > state.bands[i] ? 0.35 : 0.1);
        // Louder bands travel faster, so speech visibly stirs the waves
        state.phases[i] += dt * layer.drift * (live ? 0.6 + 5 * state.bands[i] : 0.35);
      });

      // Resting breath does not need 60 fps
      state.count++;
      if (live || state.count % 2 === 0) draw(ctx, state.w, state.h, state, t);

      if ((!on && state.fade < 0.004) || !state.visible) {
        ctx.clearRect(0, 0, state.w, state.h);
        if (!on) state.fade = 0;
        state.running = false;
        state.last = 0;
        return;
      }
      state.frame = requestAnimationFrame(step);
    };

    wake.current = () => {
      if (reduced) {
        still();
        return;
      }
      if (state.running || !state.visible || !state.w) return;
      if (!props.current.active && state.fade < 0.004) return;
      state.running = true;
      state.frame = requestAnimationFrame(step);
    };

    resize();
    const resizer = new ResizeObserver(() => {
      resize();
      if (reduced) still();
      wake.current();
    });
    resizer.observe(canvas);

    const watcher = new IntersectionObserver(([entry]) => {
      state.visible = entry.isIntersecting;
      visibility.current?.(entry.isIntersecting);
      wake.current();
    });
    watcher.observe(canvas);

    wake.current();

    return () => {
      cancelAnimationFrame(state.frame);
      state.running = false;
      resizer.disconnect();
      watcher.disconnect();
      wake.current = () => {};
    };
  }, []);

  useEffect(() => {
    props.current = { analyser, active, playing };
    wake.current();
  }, [analyser, active, playing]);

  return (
    <div
      className={`voice-halo voice-halo--${size}`}
      data-state={!active ? 'off' : playing ? 'speaking' : 'resting'}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="voice-halo__canvas" />
    </div>
  );
}

export default VoiceHalo;
